import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { SledTeam } from '@sdd-maturity/shared-types';
import { useApi } from '../hooks/useApi';
import { useRecentSearches, type RecentSearch } from '../hooks/useRecentSearches';
import { buildScopePath } from '../lib/hierarchy-route';
import './SearchLauncher.css';

/**
 * FR-4: global cross-tier search launcher. A ⌘K-style command palette that
 * filters the already-cached `getAllTeams()` tree across all four tiers and
 * jumps to a node's canonical typed URL (AC-4.3 via {@link buildScopePath}).
 *
 * Rendered inline in the global bar (Airbnb-style): the header field *is* the
 * search input, and its results drop down anchored directly beneath it at the
 * same width — not a separate centered modal. Scope-independent (AC-4.5): it never
 * reflects the current page — the page-header breadcrumb owns "where am I".
 * Client-side only (AC-4.4): the same cached tree the comboboxes use; the filter
 * runs in-memory, so typing issues no network request.
 */

const MAX_RESULTS = 50;

/** A phantom tier-4 team duplicates its parent Release Train's name — hidden from results. */
function isPhantom(team: SledTeam, byNumber: Map<string, SledTeam>): boolean {
  if (team.tier !== 4) return false;
  const parent = team.parentNumber ? byNumber.get(team.parentNumber) : undefined;
  return !!parent && parent.tier === 3 && team.name.trim().toLowerCase() === parent.name.trim().toLowerCase();
}

/** Walk parentNumber upward to build the ancestor trail (root → immediate parent). */
function ancestorTrail(team: { parentNumber?: string }, byNumber: Map<string, SledTeam>): string[] {
  const trail: string[] = [];
  let current = team.parentNumber ? byNumber.get(team.parentNumber) : undefined;
  // Guard against cycles / missing links with a tier-bounded walk (max 3 ancestors).
  let guard = 0;
  while (current && guard < 4) {
    trail.unshift(current.name);
    current = current.parentNumber ? byNumber.get(current.parentNumber) : undefined;
    guard += 1;
  }
  return trail;
}

export function SearchLauncher(): React.JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // field is active → scrim + dropdown eligible to show
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  const { recents, addRecent } = useRecentSearches();

  const { data, isLoading } = useApi<{ teams: SledTeam[] }>((client, token) => client.getAllTeams(token), []);
  const teams = useMemo(() => data?.teams ?? [], [data]);
  const byNumber = useMemo(() => new Map(teams.map((t) => [t.number, t])), [teams]);

  // Global ⌘K / Ctrl+K focuses the inline field from anywhere in the app.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // AC-4.1: cross-tier name match over the cached tree, phantoms excluded, capped for render perf.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return teams
      .filter((t) => !isPhantom(t, byNumber) && t.name.toLowerCase().includes(q))
      .sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name))
      .slice(0, MAX_RESULTS);
  }, [query, teams, byNumber]);

  // Keep the highlight in range as results change, and reset it whenever the field
  // opens (so the recents list starts highlighted at the top on a fresh focus).
  useEffect(() => {
    setHighlighted(0);
  }, [query]);
  useEffect(() => {
    if (open) setHighlighted(0);
  }, [open]);

  // Close when a pointer press lands outside the launcher (scrim or elsewhere).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  // ESC closes even if focus has drifted off the input while the panel is open
  // (e.g. after a scrim/outside click blurred the field). When the input itself
  // is focused, onInputKeyDown owns Escape — it keeps the two-step clear-then-close.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && document.activeElement !== inputRef.current) {
        e.preventDefault();
        dismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  function dismiss(): void {
    setOpen(false);
    inputRef.current?.blur();
  }

  function select(node: RecentSearch): void {
    addRecent(node); // remember this pick so it surfaces on the next empty focus.
    navigate(buildScopePath(node)); // AC-4.3: typed URL, default 'overview' lens.
    setQuery('');
    dismiss();
  }

  // Once the user types, the results list (real matches or "No matches") is shown.
  // On an empty-focus with prior selections, offer the recent list instead so users
  // can pick up where they left off; with no history the field stays bare (unchanged).
  const hasContent = query.trim().length > 0;
  const showResults = open && hasContent;
  const showRecents = open && !hasContent && recents.length > 0;
  const showPanel = showResults || showRecents;
  // The list keyboard nav + highlight + aria-activedescendant operate on.
  const options: RecentSearch[] = showRecents ? recents : results;

  // Keep the active option (aria-activedescendant) visible as arrow keys move it:
  // with up to MAX_RESULTS rows in the scroll panel it can otherwise slide offscreen.
  // `block: 'nearest'` is a no-op when the row is already visible, so mouse-hover
  // (which also moves `highlighted`) doesn't cause jumps.
  useEffect(() => {
    if (!showPanel) return;
    panelRef.current?.querySelector('[data-highlighted]')?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, showPanel]);

  // Screen-reader status (WCAG 4.1.3): announce how many results/recents are shown
  // as the user types. Polite + atomic so rapid keystroke updates coalesce.
  let announcement = '';
  if (showRecents) {
    announcement = `${recents.length} recent search${recents.length === 1 ? '' : 'es'}`;
  } else if (showResults) {
    if (isLoading && results.length === 0) announcement = 'Searching…';
    else if (results.length === 0) announcement = 'No matches';
    else announcement = `${results.length} result${results.length === 1 ? '' : 's'}`;
  }

  // Close when keyboard focus leaves the field for a real element outside the
  // launcher (Tab-away). A null relatedTarget — a mouse press on an option (the
  // rows aren't focusable) or the window losing focus — is intentionally left to
  // the pointerdown-outside + select() paths, so a click isn't cut off by blur
  // racing it (the row would unmount before its onClick fires).
  function onInputBlur(e: React.FocusEvent<HTMLInputElement>): void {
    if (e.relatedTarget && !rootRef.current?.contains(e.relatedTarget)) {
      setOpen(false);
    }
  }

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (query) setQuery(''); // first Esc clears the query…
      else dismiss(); //          …a second closes the field.
      return;
    }
    if (options.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((i) => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const node = options[highlighted];
      if (node) select(node);
    }
  }

  // Results and recents render as identical option rows; `i` is the flat index
  // within `options` so keyboard highlighting lines up with the shared list.
  function renderOption(node: RecentSearch, i: number): React.JSX.Element {
    const trail = ancestorTrail(node, byNumber);
    return (
      <li
        key={`${node.tier}-${node.number}`}
        id={`${listboxId}-opt-${i}`}
        role="option"
        aria-selected={i === highlighted}
        data-highlighted={i === highlighted ? '' : undefined}
        className="search-launcher-item"
        onMouseEnter={() => setHighlighted(i)}
        onClick={() => select(node)}
      >
        <span className="search-launcher-item-primary">
          <span className="search-launcher-item-name">{node.name}</span>
          <span className="badge badge-info search-launcher-tier-chip">{node.tierLabel}</span>
        </span>
        {trail.length > 0 && (
          <span className="search-launcher-item-meta">
            <span className="search-launcher-trail">{trail.join(' › ')}</span>
          </span>
        )}
      </li>
    );
  }

  return (
    <div ref={rootRef} className={`search-launcher${open ? ' is-open' : ''}`}>
      {/* The field sits on the header's dark Cox-blue gradient, so it borrows the
          dark-mode token values regardless of page theme. Scoped to the field only —
          the results panel and scrim below are siblings (not descendants), so they
          deliberately keep the page theme and read as a normal light-mode dropdown. */}
      <div className="search-launcher-field" data-theme="dark">
        <SearchIcon size={16} />
        <input
          ref={inputRef}
          className="search-launcher-input"
          type="text"
          role="combobox"
          aria-label="Search portfolios, delivery streams, release trains, and teams"
          aria-autocomplete="list"
          aria-expanded={showPanel}
          aria-controls={listboxId}
          aria-activedescendant={showPanel && options.length > 0 ? `${listboxId}-opt-${highlighted}` : undefined}
          placeholder="Find a team, release train, delivery stream…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={onInputBlur}
          onKeyDown={onInputKeyDown}
          autoComplete="off"
        />
        {!open && <kbd className="search-launcher-kbd">⌘K</kbd>}
      </div>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      {showPanel && (
        <>
          {/* Dim the rest of the page; a press here (outside the field) closes it.
              The scrim lives inside the launcher root, so the document click-outside
              guard treats it as "inside" — wire its own press to dismiss(). */}
          <div className="search-launcher-scrim" aria-hidden="true" onPointerDown={dismiss} />
          <ul ref={panelRef} id={listboxId} role="listbox" className="search-launcher-panel">
            {showRecents ? (
              <>
                {/* aria-hidden label: the listbox exposes only the option rows. */}
                <li className="search-launcher-section-label" aria-hidden="true">
                  Recent
                </li>
                {recents.map((node, i) => renderOption(node, i))}
              </>
            ) : (
              <>
                {results.length === 0 &&
                  (isLoading ? (
                    <li className="search-launcher-empty">Loading…</li>
                  ) : (
                    <li className="search-launcher-empty">No matches</li>
                  ))}
                {results.map((node, i) => renderOption(node, i))}
              </>
            )}
          </ul>
        </>
      )}
    </div>
  );
}

function SearchIcon({ size = 14 }: { size?: number }): React.JSX.Element {
  return (
    <MagnifyingGlassIcon
      aria-hidden="true"
      className="search-launcher-icon"
      style={{ width: size, height: size }}
    />
  );
}
